<template>
  <div class="home">
    <section class="hero">
      <div class="hero-copy">
        <h1 class="hero-title">在 YOJ，练习编程与算法</h1>
        <p class="hero-desc">从一道题开始，清晰记录每一次思考与提交，让进步自然发生。</p>
        <div class="hero-actions">
          <el-button type="primary" size="large" @click="router.push('/problems')">开始刷题</el-button>
          <el-button size="large" @click="router.push('/submissions')">提交记录</el-button>
        </div>
      </div>
      <div class="hero-symbol" aria-hidden="true">
        <span>&lt;/&gt;</span>
      </div>
    </section>

    <section class="stats-section" aria-label="平台统计">
      <el-row :gutter="24">
        <el-col :xs="24" :sm="8" :span="8">
          <el-card shadow="never" class="stat-card">
            <div class="stat-value">{{ stats.problems }}</div>
            <div class="stat-label">题目总数</div>
          </el-card>
        </el-col>
        <el-col :xs="24" :sm="8" :span="8">
          <el-card shadow="never" class="stat-card">
            <div class="stat-value">{{ stats.submissions }}</div>
            <div class="stat-label">提交次数</div>
          </el-card>
        </el-col>
        <el-col :xs="24" :sm="8" :span="8">
          <el-card shadow="never" class="stat-card">
            <div class="stat-value">{{ stats.users }}</div>
            <div class="stat-label">用户数</div>
          </el-card>
        </el-col>
      </el-row>
    </section>

    <section class="links-section">
      <div class="section-heading">
        <h2 class="section-title">快速入口</h2>
        <p class="section-desc">选择下一步，继续你的练习。</p>
      </div>
      <el-row :gutter="24">
        <el-col :xs="24" :sm="8" :span="8">
          <el-card
            shadow="never"
            class="link-card"
            role="link"
            tabindex="0"
            @click="router.push('/problems')"
            @keydown.enter="router.push('/problems')"
            @keydown.space.prevent="router.push('/problems')"
          >
            <div class="link-icon orange"><el-icon :size="26"><List /></el-icon></div>
            <div class="link-title">题目列表</div>
            <div class="link-desc">浏览所有可用题目，开始挑战</div>
          </el-card>
        </el-col>
        <el-col :xs="24" :sm="8" :span="8">
          <el-card
            shadow="never"
            class="link-card"
            role="link"
            tabindex="0"
            @click="router.push('/submissions')"
            @keydown.enter="router.push('/submissions')"
            @keydown.space.prevent="router.push('/submissions')"
          >
            <div class="link-icon pink"><el-icon :size="26"><Document /></el-icon></div>
            <div class="link-title">提交记录</div>
            <div class="link-desc">查看你的代码提交历史与判题结果</div>
          </el-card>
        </el-col>
        <el-col :xs="24" :sm="8" :span="8">
          <el-card
            shadow="never"
            class="link-card"
            role="link"
            tabindex="0"
            @click="handleAdminClick"
            @keydown.enter="handleAdminClick"
            @keydown.space.prevent="handleAdminClick"
          >
            <div class="link-icon emerald"><el-icon :size="26"><Setting /></el-icon></div>
            <div class="link-title">管理后台</div>
            <div class="link-desc">管理员入口：管理题目与用户</div>
          </el-card>
        </el-col>
      </el-row>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { List, Document, Setting } from '@element-plus/icons-vue'
import { getOverview } from '@/api/pingtaitongji'
import { useLoginUserStore } from '@/store/loginUser'

const router = useRouter()
const loginUserStore = useLoginUserStore()

const stats = ref({ problems: '-', submissions: '-', users: '-' })

async function fetchStats() {
  try {
    const res = await getOverview()
    if (res.code === 0 && res.data) {
      stats.value.problems = res.data.questionCount ?? '-'
      stats.value.submissions = res.data.submitCount ?? '-'
      stats.value.users = res.data.userCount ?? '-'
    }
  } catch (e) {
    console.error('获取平台统计失败', e)
  }
}

function handleAdminClick() {
  const role = loginUserStore.loginUser?.userRole
  if (role === 'admin') {
    router.push('/manage/problems')
  } else if (role === 'notLogin') {
    ElMessage.info('请先登录后再访问管理后台')
  } else {
    ElMessage.warning('无管理员权限')
  }
}

onMounted(fetchStats)
</script>

<style scoped>
.home {
  max-width: 1152px;
  margin: 0 auto;
  padding-bottom: 32px;
}

.hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 48px;
  min-height: 340px;
  padding: 56px 64px;
  overflow: hidden;
  border-radius: 32px;
  border: 1px solid #d6d3d1;
  background: #ffffff;
  box-shadow: 0 18px 36px -18px rgba(120, 113, 108, 0.42), 0 5px 14px -8px rgba(120, 113, 108, 0.32);
}

.hero-copy {
  max-width: 650px;
}

.hero-title {
  max-width: 600px;
  color: var(--yoj-text);
  font-size: clamp(36px, 4.5vw, 52px);
  font-weight: 600;
  line-height: 1.22;
  letter-spacing: -0.035em;
}

.hero-desc {
  max-width: 560px;
  margin-top: 20px;
  color: var(--yoj-text-secondary);
  font-size: 16px;
  line-height: 1.9;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 32px;
}

.hero-actions .el-button {
  min-width: 128px;
  margin: 0;
}

.hero-symbol {
  display: grid;
  place-items: center;
  width: 190px;
  height: 190px;
  flex: 0 0 190px;
  color: #c2410c;
  border-radius: 50%;
  background: #fff7ed;
  box-shadow: 0 20px 25px -5px rgba(194, 65, 12, 0.2);
}

.hero-symbol span {
  font-family: Consolas, monospace;
  font-size: 46px;
  font-weight: 700;
  letter-spacing: -0.1em;
}

.stats-section {
  margin: 32px 0 48px;
}

.stats-section .el-col,
.links-section .el-col {
  margin-bottom: 24px;
}

.stat-card :deep(.el-card__body) {
  padding: 28px 32px;
}

.stat-value {
  color: #9a3412;
  font-family: Consolas, monospace;
  font-size: 36px;
  font-weight: 700;
  letter-spacing: -0.04em;
}

.stat-label {
  margin-top: 8px;
  color: var(--yoj-text-secondary);
  font-size: 14px;
}

.links-section {
  padding-top: 32px;
  border-top: 1px solid #d6d3d1;
}

.section-heading {
  margin-bottom: 24px;
}

.section-title {
  color: var(--yoj-text);
  font-size: 26px;
  font-weight: 600;
}

.section-desc {
  margin-top: 8px;
  color: var(--yoj-text-muted);
  font-size: 14px;
  line-height: 1.7;
}

.link-card {
  min-height: 220px;
  cursor: pointer;
}

.link-card :deep(.el-card__body) {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  height: 100%;
  padding: 28px;
}

.link-icon {
  display: grid;
  place-items: center;
  width: 54px;
  height: 54px;
  margin-bottom: 24px;
  border-radius: 50%;
  transition: transform 200ms ease-in-out, background-color 200ms ease-in-out;
}

.link-icon.orange {
  color: #c2410c;
  background: #fff7ed;
}

.link-icon.pink {
  color: #a16207;
  background: #fefce8;
}

.link-icon.emerald {
  color: #047857;
  background: #ecfdf5;
}

.link-title {
  margin-bottom: 10px;
  color: var(--yoj-text);
  font-size: 20px;
  font-weight: 600;
}

.link-desc {
  max-width: 260px;
  color: var(--yoj-text-secondary);
  font-size: 14px;
  line-height: 1.75;
}

@media (hover: hover) {
  .link-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 20px 25px -5px rgba(194, 65, 12, 0.2), 0 8px 10px -6px rgba(194, 65, 12, 0.2);
  }

  .link-card:hover .link-icon {
    transform: translateY(-2px);
  }
}

.link-card:active {
  transform: scale(0.98);
  box-shadow: inset 0 2px 4px rgba(41, 37, 36, 0.08);
}

@media (max-width: 900px) {
  .hero {
    padding: 48px;
  }

  .hero-symbol {
    width: 140px;
    height: 140px;
    flex-basis: 140px;
  }

  .hero-symbol span {
    font-size: 36px;
  }
}

@media (max-width: 640px) {
  .hero {
    flex-direction: column;
    align-items: flex-start;
    gap: 32px;
    min-height: 0;
    padding: 36px 28px;
    border-radius: 24px;
  }

  .hero-title {
    font-size: 34px;
  }

  .hero-desc {
    font-size: 14px;
  }

  .hero-symbol {
    width: 92px;
    height: 92px;
    flex-basis: 92px;
    align-self: flex-end;
  }

  .hero-symbol span {
    font-size: 26px;
  }

  .stats-section {
    margin-bottom: 32px;
  }

  .section-title {
    font-size: 23px;
  }

  .link-card {
    min-height: 0;
  }
}

@media (max-width: 380px) {
  .hero-actions {
    flex-direction: column;
  }

  .hero-actions .el-button {
    width: 100%;
  }
}
</style>
